import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";
import { LanguageProvider } from './contexts/LanguageContext';
import Sidebar from './components/layout/Sidebar';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import About from './pages/About';
import Privacy from './pages/Privacy';
import SalesTerms from './pages/SalesTerms';
import Contact from './pages/Contact';
import Profile from './pages/Profile';
import Onboarding from './pages/Onboarding';
import Conversations from './pages/Conversations';
import NewConversation from './pages/NewConversation';
import Resources from './pages/Resources';
import ConversationView from './pages/ConversationView';
import Account from './pages/Account';
import SoknadWizard from './pages/SoknadWizard';
import SoknadPreview from './pages/SoknadWizard/SoknadPreview';
import SoknadOverview from './pages/SoknadWizard/SoknadOverview';
import KeyedByDraftId from './pages/SoknadWizard/KeyedByDraftId';
import Subscription from './pages/Subscription';
import SubscriptionConfirm from './pages/SubscriptionConfirm';
import AdminVillage from './admin';
import AuthPopup from './components/popups/AuthPopup';
import AppLoading from './components/common/AppLoading';
import FeedbackWidget from './components/FeedbackWidget';
import ConsentBanner from './components/ConsentBanner';
import { initAnalytics } from './utils/analytics';
import AuthInitializer from './auth/AuthInitializer';
import { debugAuthState } from './utils/authDebug';
import { msalInstance } from './auth/msalConfig';
import { useProfile, ProfileProvider } from './contexts/ProfileContext';
import { useSubscription, SubscriptionProvider } from './contexts/SubscriptionContext';
import usePageTracking from './hooks/usePageTracking';

// Component to enable page tracking inside Router context
const PageTracker = () => {
  usePageTracking();
  return null;
};

// This component uses the profile context
const AppContent = () => {
  const { accounts } = useMsal();
  const isAuthenticated = accounts.length > 0;
  const { profile, loaded: profileLoaded, refresh } = useProfile();
  const {
    hasAccess,
    gateEnabled,
    fetched: subscriptionFetched,
    refresh: refreshSubscription
  } = useSubscription();
  const [isPopupActive, setIsPopupActive] = useState(false);

  useEffect(() => {
    if (isAuthenticated && isPopupActive) {
      setIsPopupActive(false);
    }
  }, [isAuthenticated, isPopupActive]);

  // Set active account and refresh profile when auth state changes
  useEffect(() => {
    // If needed, run debug in development
    if (process.env.NODE_ENV === 'development') {
      debugAuthState();
    }

    // If we have accounts but no active account, set the first one as active
    if (accounts.length > 0) {
      const activeAccount = msalInstance.getActiveAccount();
      if (!activeAccount) {
        msalInstance.setActiveAccount(accounts[0]);
      }

      // Re-fetch profile after login — ProfileContext may have loaded
      // an empty placeholder before the user authenticated
      if (profileLoaded && !profile?.userId) {
        refresh();
      }

      // Same for the subscription status — SubscriptionContext skips its
      // initial fetch when it mounts before login. Concurrent duplicates
      // are deduped inside the provider.
      if (!subscriptionFetched) {
        refreshSubscription();
      }
    }
    // We intentionally key only on `accounts` (auth-state change). profileLoaded /
    // profile?.userId / subscriptionFetched are read as one-shot guards, and the
    // refresh calls mutate them — adding them (or possibly-unstable refresh fns)
    // would re-trigger this effect and can loop the post-login re-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  const handleClosePopup = () => {
    setIsPopupActive(false);
  };

  const handleShowPopup = () => {
    setIsPopupActive(true);
  };

  // While we're still fetching the profile, show a spinner.
  // Also treat an empty placeholder profile (no userId) as "not loaded" —
  // this happens when the user just authenticated but ProfileContext
  // mounted before login and only has the {} placeholder.
  // The subscription status must be in before routing too — otherwise a
  // gated user would see a flash of the ungated app after login.
  const profileStale = isAuthenticated && profileLoaded && !profile?.userId;
  if (isAuthenticated && (!profileLoaded || profileStale || !subscriptionFetched)) {
    return <AppLoading />;
  }

  // Subscription gate: only when the backend reports the gate as enabled.
  // hasAccess is the server-computed paid-through verdict from /status
  // (backend lib/subscriptionAccess.js) — the same rule the API gate
  // enforces, never re-derived here. With gateEnabled false, routing
  // behaves exactly as before this seam.
  const needsSubscription = isAuthenticated && gateEnabled && !hasAccess;

  // Wizard is needed if neither profileComplete nor wizardSkipped is set
  const needsWizard = isAuthenticated && profileLoaded && !profile?.profileComplete && !profile?.wizardSkipped;

  // Auth chain for gated app routes: signed in → paid access (when the gate
  // is on) → app. /abonnement, /abonnement/bekreft, /account, /salgsvilkar
  // and /privacy stay reachable without a subscription.
  const requireSubscribed = (element) =>
    !isAuthenticated
      ? <Navigate to="/" replace />
      : needsSubscription
        ? <Navigate to="/abonnement" replace />
        : element;

  return (
    <Router>
      <PageTracker />
      <div className="flex h-screen-dvh">
        {/* Hide sidebar during onboarding and while the subscription gate is up */}
        {!needsWizard && !needsSubscription && <Sidebar setIsPopupActive={handleShowPopup} />}
        {/* Mobile top offset = fixed header height (63px) + 19px breathing
            room, tracking the header's py-3 row in Sidebar.js. The header
            additionally grows by the safe-area inset on notched phones; the
            margin mirrors that (margin, not padding, because padding can't
            hold two values and calc()+env() breaks postcss-calc). The old
            flat 70px ignored the inset, so content slid under the header. */}
        <div id="main-scroll-container" className={`flex-1 bg-background p-4 pt-[82px] mt-[env(safe-area-inset-top)] md:mt-0 md:pt-8 md:p-8 overflow-y-auto ${(needsWizard || needsSubscription) ? '' : 'md:ml-64'}`}>
          <Routes>
            {/* Onboarding route — subscription comes before the wizard */}
            <Route
              path="/onboarding/*"
              element={needsSubscription ? <Navigate to="/abonnement" replace /> : <Onboarding />}
            />

            {/* Root */}
            <Route
              path="/"
              element={
                isAuthenticated
                  ? needsSubscription
                    ? <Navigate to="/abonnement" replace />
                    : needsWizard
                      ? <Navigate to="/onboarding" replace />
                      : <Navigate to="/home" replace />
                  : <Home isPopupActive={isPopupActive} setIsPopupActive={handleShowPopup} />
              }
            />
            <Route
              path="/home"
              element={
                isAuthenticated
                  ? needsSubscription
                    ? <Navigate to="/abonnement" replace />
                    : needsWizard
                      ? <Navigate to="/onboarding" replace />
                      : <Dashboard />
                  : <Navigate to="/" replace />
              }
            />
            {/* Redirect /dashboard to /home for backwards compatibility */}
            <Route path="/dashboard" element={<Navigate to="/home" replace />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/salgsvilkar" element={<SalesTerms />} />
            <Route path="/contact" element={<Contact />} />
            {/* Subscription routes — reachable without an active subscription */}
            <Route path="/abonnement" element={isAuthenticated ? <Subscription /> : <Navigate to="/" replace />} />
            <Route path="/abonnement/bekreft" element={isAuthenticated ? <SubscriptionConfirm /> : <Navigate to="/" replace />} />
            <Route path="/profile" element={requireSubscribed(<Profile />)} />
            <Route path="/conversations" element={requireSubscribed(<Conversations />)} />
            <Route path="/new-conversation" element={requireSubscribed(<NewConversation />)} />
            <Route path="/resources" element={requireSubscribed(<Resources />)} />
            <Route path="/conversations/:id" element={requireSubscribed(<ConversationView />)} />
            {/* Søknad routes. /soknad/:type is the per-child overview: every
                way in without a draft id picks the child there. A draft opens
                by id. The id-less preview (old links) goes to the overview. */}
            <Route
              path="/soknad/:type/preview"
              element={<Navigate to=".." relative="path" replace />}
            />
            <Route
              path="/soknad/:type"
              element={requireSubscribed(<SoknadOverview />)}
            />
            <Route
              path="/soknad/:type/:draftId/preview"
              element={requireSubscribed(<KeyedByDraftId><SoknadPreview /></KeyedByDraftId>)}
            />
            <Route
              path="/soknad/:type/:draftId"
              element={requireSubscribed(<KeyedByDraftId><SoknadWizard /></KeyedByDraftId>)}
            />
            {/* Account stays reachable without a subscription (GDPR erasure
                lives there); the page itself hides non-settings tabs then. */}
            <Route
              path="/account/*"
              element={isAuthenticated ? <Account /> : <Navigate to="/" />}
            />
            {/* Admin village dashboard — self-contained under src/admin. Admin-ness
                is the backend's call: the page asks /api/admin/metrics and a 403
                sends the user home. Reachable without a subscription, like /account. */}
            <Route
              path="/admin"
              element={isAuthenticated ? <AdminVillage /> : <Navigate to="/" />}
            />
          </Routes>
        </div>
      </div>
      {isPopupActive && <AuthPopup onClose={handleClosePopup} />}
      {isAuthenticated && <FeedbackWidget />}
      {/* On all pages, public ones included — GA4 runs there too if consented */}
      <ConsentBanner />
    </Router>
  );
};

// Main App component that provides the context
const App = () => {
  // Load GA4 only if analytics consent is already stored as accepted,
  // and react to consent changes for the rest of the session.
  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <LanguageProvider>
      <ProfileProvider>
        <SubscriptionProvider>
          <AuthInitializer />
          <AppContent />
        </SubscriptionProvider>
      </ProfileProvider>
    </LanguageProvider>
  );
};

export default App;